class SubscriptionOnetimeWidget extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.cart = document.querySelector('cart-drawer');
    this.addToCartButton = this.querySelector('#addToCartButton');
    this.nextProductVariantId = this.addToCartButton.dataset.nextProductVariant.split('/').pop();
    this.sellingPlanInput = Object.assign(document.createElement('input'), { type: 'hidden', name: 'selling_plan' });
    this.purchaseTypes = Object.freeze({
      ONETIME: 'ONETIME',
      SUBSCRIPTION: 'SUBSCRIPTION',
    });
    this.frequencySelect = this.querySelector('select');
    this.currentPurchaseType = this.purchaseTypes.ONETIME;
    this.alreadyDiscounted = this.dataset.alreadyDiscounted === 'true';
    this.variantsInformation = this.#buildVariantsInformation();
    this.variantChangeListener = this.#handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.#addEventListeners();
    this.#preselectUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.variantChangeListener);
  }

  #addEventListeners() {
    this.querySelectorAll('.purchase_type').forEach((purchaseType) => {
      purchaseType.addEventListener('click', () => {
        this.#clearClassOfAttribute('purchase_type', 'selected');
        purchaseType.setAttribute('selected', '');

        if (purchaseType.id == 'subscriptionButton') {
          if (!this.sellingPlanInput.value.length) {
            if (this.frequencySelect) {
              this.sellingPlanInput.value = this.frequencySelect.selectedOptions[0].dataset.sellingPlanId;
            } else {
              this.sellingPlanInput.value = purchaseType.dataset.sellingPlanId;
            }
          }
          this.addToCartButton.setAttribute('data-discount-code', purchaseType.dataset.discountCode);
          this.currentPurchaseType = this.purchaseTypes.SUBSCRIPTION;
        } else {
          this.addToCartButton.removeAttribute('data-discount-code');
          this.currentPurchaseType = this.purchaseTypes.ONETIME;
        }
      });
    });

    this.addToCartButton.addEventListener('click', () => {
      this.#addToCart(this.addToCartButton.dataset.discountCode || null);
    });

    this.querySelector('select')?.addEventListener('change', (event) => {
      this.sellingPlanInput.value = event.target.selectedOptions[0].dataset.sellingPlanId;
    });

    document.addEventListener('variant:change', this.variantChangeListener);
  }

  #handleVariantChange(event) {
    this.#handleVariantUpdate(event.detail.variant.id);
  }

  #preselectUpdate() {
    const purchaseOption = this.querySelector('div.purchase_type[selected]');
    if (purchaseOption && purchaseOption.id == 'subscriptionButton') {
      purchaseOption.click();
      this.#preselectFrequency();
    }
  }

  #preselectFrequency() {
    const firstFrequency = this.querySelector('.subscription_option');
    if (firstFrequency) {
      firstFrequency.click();
    }
  }

  async #addToCart(discountCode = null) {
    try {
      this.#setLoading(true);
      const formData = this.#buildFormData();
      const standardCart = new StandardCart(this, formData);
      const cart = await standardCart.addToCart();

      if (!this.alreadyDiscounted && cart) {
        const codes = cart.discount_codes.map((object) => object.code);
        const updatedCodes =
          this.currentPurchaseType == 'SUBSCRIPTION' && Boolean(discountCode)
            ? [...codes, discountCode]
            : codes.filter((code) => code != discountCode);

        const uniqueCodes = [...new Set(updatedCodes)];

        const allNewExist = uniqueCodes.every((code) => codes.includes(code));
        const allOldExist = codes.every((code) => uniqueCodes.includes(code));

        if (!(allNewExist && allOldExist)) {
          await standardCart.updateCartDiscounts(uniqueCodes);
        }
      }
    } catch (error) {
      console.log('Error while adding this product to cart: ', error);
    }
    this.#setLoading(false);
  }

  #buildFormData() {
    this.#syncSelectedVariantFromProductInfo();
    this.#prepareForm();

    const formData = new FormData(this.form);
    if (this.currentPurchaseType == 'SUBSCRIPTION' && this.nextProductVariantId) {
      formData.append('properties[_next_product_variant_id]', +this.nextProductVariantId);
    }
    return formData;
  }

  #prepareForm() {
    if (this.form.contains(this.sellingPlanInput)) {
      if (this.currentPurchaseType == 'ONETIME') {
        this.form.removeChild(this.sellingPlanInput);
      }
    } else {
      if (this.currentPurchaseType == 'SUBSCRIPTION') {
        this.form.appendChild(this.sellingPlanInput);
      }
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart },
        }),
      );
      return cart;
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
    return null;
  }

  #setLoading(loading) {
    const spinner = this.addToCartButton.querySelector('.loading__spinner');

    if (loading) {
      spinner.classList.remove('hidden');
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('loading');
    } else {
      spinner.classList.add('hidden');
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('loading');
    }
  }

  #clearClassOfAttribute(className, attributeName) {
    this.querySelectorAll(`.${className}`).forEach((oneType) => {
      oneType.removeAttribute(attributeName);
    });
  }

  #handleVariantUpdate(variantId) {
    const matchingVariant = this.variantsInformation[variantId];
    if (!matchingVariant) return;

    this.nextProductVariantId = matchingVariant.nextProductVariantId;
    this.form.querySelector('[name="id"]').value = variantId;

    this.#updateVariantPriceUI(matchingVariant);
    this.#updateFrequncySelect(matchingVariant.sellingPlans);
  }

  #updateVariantPriceUI(matchingVariant) {
    this.querySelector('#subscriptionButton .extra_message').textContent = matchingVariant.saveThisAmountText;
    this.querySelector('#subscriptionButton .purchase_price').textContent =
      `£${(matchingVariant.discountedPrice / 100).toFixed(2)}`;
    this.querySelector('#subscriptionButton .strikethrough_price').textContent =
      `£${(matchingVariant.price / 100).toFixed(2)}`;
    this.querySelector('#onetimeButton .onetime_price').textContent = `£${(matchingVariant.price / 100).toFixed(2)}`;
  }

  #updateFrequncySelect(sellingPlans) {
    if (!this.frequencySelect) return;

    this.frequencySelect.replaceChildren();

    sellingPlans.forEach((plan, index) => {
      const option = document.createElement('option');
      option.dataset.sellingPlanId = plan.id;
      option.textContent = plan.textContent;

      this.frequencySelect.appendChild(option);
    });
  }

  #buildVariantsInformation() {
    const variantsInformation = {};
    const siblingDataRoot = this.previousElementSibling?.matches('[data-role="subscription-variant-data"]')
      ? this.previousElementSibling
      : null;
    const dataRoot = this.querySelector('[data-role="subscription-variant-data"]') || siblingDataRoot;
    if (!dataRoot) return variantsInformation;

    dataRoot.querySelectorAll('[data-role="variant"]').forEach((variantNode) => {
      const variantId = variantNode.dataset.variantId;
      if (!variantId) return;

      variantsInformation[variantId] = {
        id: Number(variantId),
        price: Number(variantNode.dataset.price) || 0,
        discountedPrice: Number(variantNode.dataset.discountedPrice) || 0,
        nextProductVariantId: variantNode.dataset.nextProductVariantId || '',
        saveThisAmountText: variantNode.dataset.saveThisAmountText || '',
        sellingPlans: Array.from(variantNode.querySelectorAll('[data-role="selling-plan"]')).map((planNode) => ({
          id: Number(planNode.dataset.planId),
          textContent: planNode.dataset.planName || '',
        })),
      };
    });

    return variantsInformation;
  }

  #syncSelectedVariantFromProductInfo() {
    const selectedVariantNode = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');

    if (!selectedVariantNode?.textContent) return;

    try {
      const selectedVariant = JSON.parse(selectedVariantNode.textContent);
      const selectedVariantId = selectedVariant?.id;
      if (!selectedVariantId) return;

      this.form.querySelector('[name="id"]').value = selectedVariantId;
      this.#handleVariantUpdate(selectedVariantId);
    } catch (error) {
      console.warn('Failed to sync selected variant from product-info', error);
    }
  }
}

if (!customElements.get('subscription-onetime-widget')) {
  window.customElements.define('subscription-onetime-widget', SubscriptionOnetimeWidget);
}
